"use client";

import { ReactNode } from "react";
import { AlertCircle, CheckCircle, AlertTriangle, Info, X } from "lucide-react";

type AlertType = "error" | "success" | "warning" | "info";

interface AlertProps {
  type: AlertType;
  message: string;
  icon?: ReactNode;
  onClose?: () => void;
  className?: string;
}

const typeStyles: Record<AlertType, string> = {
  error:
    "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200",
  success:
    "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200",
  warning:
    "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200",
  info: "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200",
};

const iconStyles: Record<AlertType, string> = {
  error: "text-red-500 dark:text-red-400",
  success: "text-green-500 dark:text-green-400",
  warning: "text-amber-500 dark:text-amber-400",
  info: "text-blue-500 dark:text-blue-400",
};

function Alert({
  type,
  message,
  icon,
  onClose,
  className = "",
}: AlertProps) {
  const defaultIcons: Record<AlertType, ReactNode> = {
    error: <AlertCircle className="w-5 h-5" />,
    success: <CheckCircle className="w-5 h-5" />,
    warning: <AlertTriangle className="w-5 h-5" />,
    info: <Info className="w-5 h-5" />,
  };
  const displayIcon = icon ?? defaultIcons[type];

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${typeStyles[type]} ${className}`}
    >
      <span className={`flex-shrink-0 mt-0.5 ${iconStyles[type]}`}>
        {displayIcon}
      </span>
      <p className="flex-1 text-sm font-medium">{message}</p>
      {onClose && (
        <button
          onClick={onClose}
          className="flex-shrink-0 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          aria-label="닫기"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export { Alert, type AlertProps, type AlertType };
